import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApis } from '../contexts/ApiContext';
import { FaArrowLeft, FaShoppingCart, FaUser, FaCalendar, FaDollarSign, FaCheckCircle, FaPrint } from 'react-icons/fa';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { orderApis } = useApis();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!user?.id || !orderApis) {
        console.log('OrderDetails - Missing user or orderApis:', { userId: user?.id, orderApis: !!orderApis });
        return;
      }

      try {
        console.log('OrderDetails - Fetching order details for order ID:', id, 'user:', user.id, 'store:', user.store_id);
        
        const orderData = await orderApis.getOrderById(id);
        
        if (orderData) {
          console.log('OrderDetails - Order data received:', orderData);
          setOrder(orderData);
        } else {
          console.log('OrderDetails - No order data returned from API');
          throw new Error('Order not found');
        }
      } catch (error) {
        console.error('OrderDetails - Error fetching order details:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id, user, orderApis]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-center">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Order</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <div className="text-gray-400 text-4xl mb-4">📋</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">The order you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body {
            background: white;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            max-width: 100% !important;
            padding: 20px !important;
          }
          .print-table {
            page-break-inside: avoid;
          }
          @page {
            margin: 1cm;
          }
        }
      `}} />
      
      <div className="max-w-4xl mx-auto px-4 py-6 print-container">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-2 sm:mr-4 no-print"
              >
                <FaArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </button>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Order Details</h1>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm sm:text-base w-full sm:w-auto no-print"
          >
            <FaPrint className="w-4 h-4" />
            Print Measurements
          </button>
        </div>

        {/* Print-Only View - Measurements */}
        <div className="hidden print:block mb-8">
          <div className="border-b-2 border-gray-800 pb-4 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">MEASUREMENTS</h1>
            <h2 className="text-2xl font-semibold mb-4">Order #{order.order_number || order.id}</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Customer:</strong> {order.customer?.first_name} {order.customer?.last_name}</p>
                <p><strong>Email:</strong> {order.customer?.email}</p>
              </div>
              <div className="text-right">
                <p><strong>Order Date:</strong> {formatDate(order.created_at)}</p>
                <p><strong>Status:</strong> {order.status}</p>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-bold mb-4">Order Items</h2>
          {order.items && order.items.length > 0 ? (
            <table className="w-full border-collapse print-table mb-6">
              <thead>
                <tr className="border-b-2 border-gray-800">
                  <th className="text-left py-2 px-2 font-semibold">Product Name</th>
                  <th className="text-center py-2 px-2 font-semibold">Qty</th>
                  {/* Get all unique input keys from all items */}
                  {(() => {
                    const allInputKeys = new Set();
                    order.items.forEach(item => {
                      if (item.inputs) {
                        Object.keys(item.inputs).forEach(key => allInputKeys.add(key));
                      }
                    });
                    return Array.from(allInputKeys).map(key => (
                      <th key={key} className="text-left py-2 px-2 font-semibold">{key}</th>
                    ));
                  })()}
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => {
                  const allInputKeys = new Set();
                  order.items.forEach(i => {
                    if (i.inputs) {
                      Object.keys(i.inputs).forEach(key => allInputKeys.add(key));
                    }
                  });
                  
                  return (
                    <tr key={index} className="border-b border-gray-300">
                      <td className="py-3 px-2">
                        <div className="font-medium">{item.product?.product_name || `Item #${item.id}`}</div>
                        {item.product?.description && (
                          <div className="text-xs text-gray-600">{item.product.description}</div>
                        )}
                      </td>
                      <td className="text-center py-3 px-2">{item.quantity}</td>
                      {Array.from(allInputKeys).map(key => (
                        <td key={key} className="py-3 px-2 text-sm">
                          {item.inputs?.[key] || '-'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No items in this order</p>
          )}
        </div>


        {/* Order Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 no-print">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                {order.status === 'completed' ? (
                  <FaCheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <FaShoppingCart className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Order #{order.order_number || order.id}
                </h2>
                <p className="text-gray-600">
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status || 'Active'}
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(order.order_customer_total)}</p>
              <p className="text-sm text-gray-500">Total Amount</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center">
              <FaCalendar className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FaUser className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium">{order.customer?.first_name} {order.customer?.last_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FaDollarSign className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Items</p>
                <p className="font-medium">{order.items?.length || 0} items</p>
              </div>
            </div>
            {order.completed_at && (
              <div className="flex items-center">
                <FaCheckCircle className="w-4 h-4 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="font-medium">{formatDate(order.completed_at)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        {order.tag && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 no-print">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Order Tag</label>
                <p className="mt-1 text-lg font-medium">{order.tag}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Order Number</label>
                <p className="mt-1">#{order.order_number || order.id}</p>
              </div>
              {order.customer?.email && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Customer Email</label>
                  <p className="mt-1">{order.customer.email}</p>
                </div>
              )}
              {order.status && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {order.order_note && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 no-print">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ship To</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{order.order_note}</p>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 no-print">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
          {order.items && order.items.length > 0 ? (
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {item.product?.product_name || `Item #${item.id}`}
                      </h4>
                      {item.product?.sku && (
                        <p className="text-sm text-gray-500">SKU: {item.product.sku}</p>
                      )}
                      <div className="mt-2 text-sm text-gray-600">
                        <p>Quantity: {item.quantity}</p>
                        <p>Unit Price: {formatCurrency(item.customer_item_total)}</p>
                        {item.status && (
                          <p>Status: <span className={`px-1 py-0.5 text-xs rounded ${getStatusColor(item.status)}`}>
                            {item.status}
                          </span></p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Show inputs if available */}
                  {item.inputs && Object.keys(item.inputs).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Specifications:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(item.inputs).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-gray-500">{key}:</span>
                            <span className="ml-1 text-gray-900">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">📦</div>
              <p className="text-gray-500">No items in this order</p>
            </div>
          )}
        </div>

        {/* Order Summary */}
        {order.discount || order.tax ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6 no-print">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(order.order_customer_subtotal)}</span>
              </div>
              {order.discount && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({order.discount.discount_type}):</span>
                  <span>-{order.discount.discount_type === 'percentage' ? `${order.discount.discount_rate}%` : formatCurrency(order.discount.discount_rate)}</span>
                </div>
              )}
              {order.tax && (
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>
                    {formatCurrency(Number(order.order_customer_subtotal) * Number(order.order_customer_tax_rate) / 100)}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(order.order_customer_total)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Order Timeline */}
        {order.status && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6 no-print">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <div>
                  <p className="font-medium">Order Created</p>
                  <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                </div>
              </div>
              {order.status === 'active' && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium">Order Active</p>
                    <p className="text-sm text-gray-500">Currently being processed</p>
                  </div>
                </div>
              )}
              {order.status === 'completed' && order.completed_at && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium">Order Completed</p>
                    <p className="text-sm text-gray-500">{formatDate(order.completed_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails; 